import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, AlertTriangle, Radar, Loader2, Sparkles } from 'lucide-react';
import ScamIQBadge from '../components/ScamIQBadge';
import { API_URL } from '../config';
import '../styles/ScamSmart.css';

const API = `${API_URL}/api`;
const SHIELD_LOGO = 'https://customer-assets.emergentagent.com/job_express-checkout-9/artifacts/gw8x19qe_Variant%201_Shield%20Only_Vector%20copy.png';

const ScamSmartRadar = () => {
  const [message, setMessage] = useState('');
  const [useAi, setUseAi] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const analyze = async (e) => {
    e?.preventDefault?.();
    setErr('');
    setResult(null);
    if (message.trim().length < 3) {
      setErr('Paste a message of at least a few words.');
      return;
    }
    if (message.length > 4000) {
      setErr('Message too long — paste up to 4,000 characters.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/scamsmart/radar`, { message, use_ai: useAi });
      setResult(data);
    } catch (e) {
      setErr('Sorry — radar could not analyse this message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ss-page" data-testid="ss-radar-page">
      <header className="ss-header">
        <div className="ss-header-inner">
          <Link to="/scamsmart/dashboard" className="ss-brand">
            <img src={SHIELD_LOGO} alt="" className="ss-brand-mark" />
            <div>
              <div className="ss-brand-text">All Secured</div>
              <div className="ss-brand-sub">Scam Radar</div>
            </div>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ScamIQBadge />
            <Link to="/scamsmart/dashboard" className="ss-header-cta">Dashboard</Link>
          </div>
        </div>
      </header>

      <div className="ss-test-wrap">
        <div className="ss-test-container">
          <Link to="/scamsmart/dashboard" className="ss-back"><ArrowLeft size={14} /> Dashboard</Link>

          <div className="ss-section-eyebrow">Scam Radar</div>
          <h1 className="ss-section-title" style={{ fontSize: 28, marginBottom: 8 }}>
            Paste a suspicious message
          </h1>
          <p className="ss-section-lede" style={{ fontSize: 15 }}>
            Before you click, reply, pay, or share a code — paste it here. ScamSmart will read the red
            flags, name the pressure tactic, and tell you the safer next step.
          </p>

          <form onSubmit={analyze} data-testid="ss-radar-form">
            <textarea
              className="ss-radar-area"
              placeholder="Paste an SMS, email, DM or marketplace message here…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="ss-radar-input"
              maxLength={4000}
            />
            <div className="ss-radar-warning">
              <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: 6, color: '#c08220' }} />
              <strong>Do not paste passwords, verification codes, bank details, card numbers, ID documents,
              or private personal information.</strong> Scam Radar only needs the suspicious message itself.
            </div>

            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#5C6B73', marginBottom: 16, cursor: 'pointer' }}>
              <input type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)} data-testid="ss-radar-ai-toggle" />
              <Sparkles size={13} color="#16B1C4" /> Use AI augmentation (falls back to rules engine if unavailable)
            </label>

            {err && <p style={{ color: '#c63d3d', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}

            <button type="submit" className="ss-btn-primary" disabled={loading} style={{ width: '100%', justifyContent: 'center' }} data-testid="ss-radar-submit">
              {loading ? <><Loader2 size={18} className="ss-spin" /> Reading the signals…</> : <><Radar size={16} /> Run Scam Radar</>}
            </button>
            <style>{`@keyframes ss-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}} .ss-spin{animation:ss-spin 1s linear infinite}`}</style>
          </form>

          {result && (
            <div className="ss-radar-result" data-testid="ss-radar-result">
              <span className={`ss-risk-pill ${result.risk_level}`}>
                {result.risk_level === 'High' && <AlertTriangle size={12} />}
                {result.risk_level} risk
              </span>
              <p style={{ margin: '0 0 12px', fontSize: 15, color: '#0A1F44', fontWeight: 600 }}>
                {result.likely_intent}
              </p>

              {result.red_flags?.length > 0 && (
                <>
                  <div style={{ fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 800, color: '#5C6B73', margin: '14px 0 6px' }}>
                    Red flags spotted
                  </div>
                  <div className="ss-radar-flags">
                    {result.red_flags.map((f, i) => <span key={i} className="ss-radar-flag">{f}</span>)}
                  </div>
                </>
              )}

              <div style={{ fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 800, color: '#5C6B73', margin: '14px 0 6px' }}>
                Pressure tactic
              </div>
              <p style={{ margin: '0 0 14px', fontSize: 14, color: '#0A1F44' }}>{result.pressure_tactic}</p>

              <div style={{ fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', fontWeight: 800, color: '#5C6B73', margin: '14px 0 6px' }}>
                Safest next step
              </div>
              <p style={{ margin: '0 0 14px', fontSize: 14, color: '#0A1F44', lineHeight: 1.55 }}>
                {result.safest_next_step}
              </p>

              <div className="ss-rule">
                <span className="ss-rule-label">All Secured rule</span>{result.rule}
              </div>

              <p style={{ fontSize: 11, color: '#5C6B73', marginTop: 18 }}>
                Engine: <strong>{result.engine === 'hybrid' ? 'Rules + AI' : 'Rules only'}</strong>
                {result.engine !== 'hybrid' && ' (AI augmentation unavailable — rules engine result shown)'}
              </p>
            </div>
          )}

          <p className="ss-disclaimer">
            Scam Radar provides a risk assessment based on red flags. A low-risk result does not guarantee a
            message is safe. Always verify important requests through official or trusted channels.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ScamSmartRadar;
